// Decorative light on the existing stripe. Tune these values independently
// of the product glows, borders, and background opacity.
const SETTINGS = {
  fieldHeight: 260,
  reach: 112,
  speed: 175,
  strandCount: 3,
  strandSpacing: 9,
  waveStretch: 1.45,
  sparksPerStrand: 2,
  glowRadius: 12,
  pixelRatioLimit: 1.25,
  fps: 30,
  colors: ['#00eaff', '#a571ff', '#ff4cce', '#ffe34c', '#ff7646'],
};

const motion = matchMedia('(prefers-reduced-motion: reduce)');
const pointer = { x: 0, y: 0, active: false, id: null, type: '' };
const strips = [];
let frame = 0;
let previousTime = 0;
let nextDraw = 0;
let scrolling = false;
let scrollTimer = 0;
let modalOpen = !!document.querySelector('dialog[open]');

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

function randomAt(x, y, seed) {
  let value = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(seed, 1442695041);
  value = Math.imul(value ^ (value >>> 13), 1274126177);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
}

// Random spatial bends morph smoothly between independently seeded shapes.
// Interpolating time avoids frame-by-frame flashes and repeated sine waves.
function noise(x, time, seed) {
  const left = Math.floor(x);
  const epoch = Math.floor(time);
  const across = x - left;
  const fraction = time - epoch;
  const blend = fraction * fraction * (3 - 2 * fraction);
  const a = randomAt(left, epoch, seed);
  const b = randomAt(left + 1, epoch, seed);
  const c = randomAt(left, epoch + 1, seed);
  const d = randomAt(left + 1, epoch + 1, seed);
  const now = a + (b - a) * across;
  const next = c + (d - c) * across;
  return (now + (next - now) * blend) * 2 - 1;
}

function filament(x1, y1, x2, y2, time, seed, amplitude) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  const segments = clamp(Math.ceil(length / 10), 12, 300);
  const points = [];
  for (let i = 0; i <= segments; i++) {
    const p = i / segments;
    const distance = p * length / SETTINGS.waveStretch;
    const bend = noise(distance / 76, time * .72, seed) * .8
      + noise(distance / 24, time * 1.8, seed + 53) * .55
      + noise(distance / 8, time * 3.1, seed + 167) * .28;
    const taper = Math.min(1, p * 12, (1 - p) * 12);
    const offset = bend * amplitude * taper;
    points.push([x1 + dx * p - dy / length * offset, y1 + dy * p + dx / length * offset]);
  }
  return points;
}

function pointAlong(points, progress) {
  const at = clamp(progress, 0, 1) * (points.length - 1);
  const lower = Math.floor(at);
  const next = points[Math.min(lower + 1, points.length - 1)];
  return points[lower].map((value, axis) => value + (next[axis] - value) * (at - lower));
}

function path(ctx, points) {
  ctx.beginPath();
  ctx.moveTo(...points[0]);
  for (let i = 1; i < points.length; i++) ctx.lineTo(...points[i]);
}

function stroke(ctx, points, color, alpha = 1, width = 1.2) {
  path(ctx, points);
  ctx.strokeStyle = color;
  // Layer translucent strokes instead of blurring every path every frame.
  // Firefox can rasterize the original shadowBlur paths on the CPU.
  ctx.shadowBlur = 0;
  ctx.globalAlpha = alpha * .06;
  ctx.lineWidth = width * 12;
  ctx.stroke();
  ctx.globalAlpha = alpha * .12;
  ctx.lineWidth = width * 5;
  ctx.stroke();
  ctx.globalAlpha = alpha;
  ctx.lineWidth = width;
  ctx.stroke();
  ctx.globalAlpha = alpha * .78;
  ctx.strokeStyle = '#fff5ff';
  ctx.lineWidth = .55;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function spark(ctx, x, y, color, strength) {
  const radius = 9 * strength;
  const glow = ctx.createRadialGradient(x, y, 0, x, y, radius);
  glow.addColorStop(0, '#ffffff');
  glow.addColorStop(.12, color);
  glow.addColorStop(1, 'transparent');
  ctx.fillStyle = glow;
  ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
}

function draw(strip, time, delta) {
  const { ctx, width, canvas } = strip;
  const mid = SETTINGS.fieldHeight / 2;
  ctx.clearRect(0, 0, width, SETTINGS.fieldHeight);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.globalCompositeOperation = 'lighter';

  const strands = [];
  for (let i = 0; i < SETTINGS.strandCount; i++) {
    const seed = strip.seed + i * 97;
    const center = mid + (i - (SETTINGS.strandCount - 1) / 2) * SETTINGS.strandSpacing;
    const y = center + noise(0, time * .43, seed + 701) * 7;
    const endY = center + noise(0, time * .37, seed + 809) * 7;
    const color = SETTINGS.colors[i % SETTINGS.colors.length];
    const gradient = ctx.createLinearGradient(0, y, Math.max(width, 1), y);
    gradient.addColorStop(0, color);
    gradient.addColorStop(.5, SETTINGS.colors[(i + 1) % SETTINGS.colors.length]);
    gradient.addColorStop(1, color);
    const points = filament(0, y, width, endY, time, seed, 19 + i * 2);
    strands.push(points);
    stroke(ctx, points, gradient, .72 + noise(0, time * .63, seed + 311) * .06, 1.15);

    // Bright charges travel on the unbroken lines instead of detached bolts.
    for (let j = 0; j < SETTINGS.sparksPerStrand; j++) {
      const drift = .8 + randomAt(i, j, strip.seed + 101) * .65;
      const phase = randomAt(j, i, strip.seed + 227);
      const progress = (time * SETTINGS.speed * drift / Math.max(width, 1)
        + phase + noise(0, time * .5, seed + j * 31) * .055 + 1) % 1;
      spark(ctx, ...pointAlong(points, progress), color, .85);
    }
  }
  // Cross-links make the long strands one continuous, connected band.
  for (let i = 0; i < strands.length - 1; i++) {
    const seed = strip.seed + i * 173;
    const progress = .12 + (noise(0, time * .31, seed + 907) + 1) * .35;
    const start = pointAlong(strands[i], progress);
    const end = pointAlong(strands[i + 1], progress + .025 + randomAt(i, 0, seed) * .04);
    stroke(ctx, filament(...start, ...end, time, seed, 6), SETTINGS.colors[i], .55, .85);
  }

  const px = pointer.x - strip.left;
  const py = pointer.y - strip.top;
  const near = pointer.active && px >= 0 && px <= width
    && Math.abs(py - mid) < SETTINGS.reach;
  const response = 1 - Math.exp(-delta * 13);
  strip.pull += ((near ? 1 : 0) - strip.pull) * response;
  if (near) {
    strip.targetX += (px - strip.targetX) * response;
    strip.targetY += (py - strip.targetY) * response;
  }
  if (strip.pull > .015) {
    const x = strip.targetX;
    const y = strip.targetY;
    for (let i = 0; i < strands.length; i++) {
      const seed = strip.seed + i * 97;
      const origin = clamp(x + (i - 1) * 98 + noise(0, time * .7, seed + 419) * 35, 0, width);
      const color = SETTINGS.colors[(i + 1) % SETTINGS.colors.length];
      const start = pointAlong(strands[i], origin / Math.max(width, 1));
      stroke(ctx, filament(...start, x, y, time, seed + 631, 15), color, strip.pull * .88, 1.3);
    }
    spark(ctx, x, y, '#b9f5ff', strip.pull * 1.55);
  }
}

function clear() {
  for (const strip of strips) {
    strip.ctx.clearRect(0, 0, strip.width, SETTINGS.fieldHeight);
    strip.pull = 0;
  }
}

function tick(now) {
  frame = 0;
  if (now >= nextDraw) {
    const delta = Math.min((now - (previousTime || now - 33)) / 1000, .08);
    previousTime = now;
    const interval = 1000 / SETTINGS.fps;
    nextDraw = now + interval - ((now - nextDraw) % interval);
    for (const strip of strips) if (strip.visible) draw(strip, now / 1000, delta);
  }
  schedule();
}

function schedule() {
  const run = !motion.matches && !document.hidden && !scrolling && !modalOpen
    && strips.some(strip => strip.visible);
  if (run && !frame) frame = requestAnimationFrame(tick);
  if (!run) {
    cancelAnimationFrame(frame);
    frame = 0;
    previousTime = 0;
    nextDraw = 0;
    if (motion.matches || document.hidden) clear();
  }
}

function measureBounds(strip) {
  const bounds = strip.canvas.getBoundingClientRect();
  strip.left = bounds.left;
  strip.top = bounds.top;
}

// Leave the last decorative frame in place while scrolling or using a dialog.
// Bounds are read after scrolling settles, never between animation style writes.
document.addEventListener('scroll', () => {
  scrolling = true;
  schedule();
  clearTimeout(scrollTimer);
  scrollTimer = setTimeout(() => {
    for (const strip of strips) measureBounds(strip);
    scrolling = false;
    schedule();
  }, 140);
}, { passive: true });
for (const dialog of document.querySelectorAll('dialog')) {
  new MutationObserver(() => {
    modalOpen = !!document.querySelector('dialog[open]');
    schedule();
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
}

const observer = new IntersectionObserver(entries => {
  for (const entry of entries) {
    const strip = strips.find(item => item.element === entry.target);
    if (strip) {
      strip.visible = entry.isIntersecting;
      if (strip.visible) measureBounds(strip);
    }
  }
  schedule();
}, { rootMargin: `${SETTINGS.fieldHeight / 2}px 0px` });

for (const element of document.querySelectorAll('.color-border')) {
  // A CDN-versioned entry point and an import can resolve to different module
  // URLs. Never attach a second lighting canvas to an already initialized band.
  if (element.querySelector('.electric-strip-canvas')) continue;
  const canvas = document.createElement('canvas');
  canvas.className = 'electric-strip-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.height = `${SETTINGS.fieldHeight}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) continue;
  element.append(canvas);
  const strip = { element, canvas, ctx, seed: Math.floor(Math.random() * 1048576), width: 0, left: 0, top: 0, visible: false, pull: 0, targetX: 0, targetY: SETTINGS.fieldHeight / 2 };
  strips.push(strip);
  const resize = new ResizeObserver(() => {
    strip.width = element.getBoundingClientRect().width;
    const ratio = Math.min(window.devicePixelRatio || 1, SETTINGS.pixelRatioLimit);
    canvas.width = Math.round(strip.width * ratio);
    canvas.height = Math.round(SETTINGS.fieldHeight * ratio);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    strip.targetX = strip.width / 2;
    strip.pull = 0;
    measureBounds(strip);
    schedule();
  });
  resize.observe(element);
  observer.observe(element);
}

function track(event) {
  if (!event.isPrimary) return;
  const wasActive = pointer.active;
  pointer.x = event.clientX;
  pointer.y = event.clientY;
  pointer.id = event.pointerId;
  pointer.type = event.pointerType;
  pointer.active = event.pointerType !== 'touch' || event.buttons > 0;
  if (!wasActive) {
    for (const strip of strips) {
      strip.targetX = pointer.x - strip.left;
      strip.targetY = pointer.y - strip.top;
    }
  }
}
document.addEventListener('pointermove', track, { passive: true });
document.addEventListener('pointerdown', track, { passive: true });
document.addEventListener('pointerup', event => {
  if (event.pointerId === pointer.id && pointer.type !== 'mouse') pointer.active = false;
}, { passive: true });
document.addEventListener('pointercancel', () => { pointer.active = false; }, { passive: true });
document.documentElement.addEventListener('pointerleave', () => { pointer.active = false; });
window.addEventListener('blur', () => { pointer.active = false; });
document.addEventListener('visibilitychange', () => {
  pointer.active = false;
  schedule();
});
motion.addEventListener('change', schedule);

// Share the same organic light treatment with the product popup perimeter.
export { SETTINGS, noise, filament, pointAlong, stroke, spark };
